/**
 * External dependencies
 */
import { useCallback, useRef, useState } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { useSnackbar } from '@web-stories-wp/design-system';
import { dataPixels, PAGE_HEIGHT, PAGE_WIDTH } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useAPI } from '../../../../app/api';
import { createLottieElementSnapshot } from '../../../../elements/lottie/contract';
import useLibrary from '../../useLibrary';
import { getLottiePlacedSize, LOTTIE_ELEMENT_TYPE } from './utils';

function useInsertLottieAnimation() {
  const {
    actions: { getStudioAnimation },
  } = useAPI();
  const { insertElement } = useLibrary((state) => ({
    insertElement: state.actions.insertElement,
  }));
  const { showSnackbar } = useSnackbar();
  const detailCache = useRef(new Map());
  const pendingAnimationIdsRef = useRef(new Set());
  const [pendingAnimationIds, setPendingAnimationIds] = useState([]);

  const setAnimationPending = useCallback((animationId, isPending) => {
    const nextPendingAnimationIds = new Set(pendingAnimationIdsRef.current);

    if (isPending) {
      nextPendingAnimationIds.add(animationId);
    } else {
      nextPendingAnimationIds.delete(animationId);
    }

    pendingAnimationIdsRef.current = nextPendingAnimationIds;
    setPendingAnimationIds(Array.from(nextPendingAnimationIds));
  }, []);

  const getAnimationDetail = useCallback(
    async (animationId) => {
      if (detailCache.current.has(animationId)) {
        return detailCache.current.get(animationId);
      }

      if (!getStudioAnimation) {
        throw new Error('Studio animation API is unavailable.');
      }

      const animationDetail = await getStudioAnimation(animationId);
      detailCache.current.set(animationId, animationDetail);

      return animationDetail;
    },
    [getStudioAnimation]
  );

  const insertLottieAnimation = useCallback(
    async (animation) => {
      const animationId =
        typeof animation === 'string' ? animation : animation?.id;

      if (!animationId || pendingAnimationIdsRef.current.has(animationId)) {
        return null;
      }

      setAnimationPending(animationId, true);

      try {
        const animationDetail = await getAnimationDetail(animationId);
        const snapshot = createLottieElementSnapshot(animationDetail);
        const placedSize = getLottiePlacedSize(snapshot);
        const isFullPage =
          placedSize.width === dataPixels(PAGE_WIDTH) &&
          placedSize.height === dataPixels(PAGE_HEIGHT);

        return insertElement(LOTTIE_ELEMENT_TYPE, {
          ...snapshot,
          ...placedSize,
          ...(isFullPage ? { x: 0, y: 0 } : {}),
          lockAspectRatio: true,
        });
      } catch {
        detailCache.current.delete(animationId);
        showSnackbar({
          message: __('Animation could not be added.', 'web-stories'),
          dismissable: true,
        });

        return null;
      } finally {
        setAnimationPending(animationId, false);
      }
    },
    [getAnimationDetail, insertElement, setAnimationPending, showSnackbar]
  );

  return {
    insertLottieAnimation,
    pendingAnimationIds,
  };
}

export default useInsertLottieAnimation;
